<script setup>
/**
 * 聚类详情 —— 路由页版本的 ClustersTab 弹窗。
 *
 * 聚类没有单条查询接口，行数据只存在于 listIntentClusters 的全量清单里。
 * 直接开链接（没有从列表点过来）时，服务端按 cluster_id 在同一份清单里过滤出这一条。
 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft } from '@lucide/vue'
import { Badge, Button, PageHeader, Section, Spinner } from '@drama/ui'

import { listIntentClusters } from '../../api/intent-videos.js'
import { queueStatusLabel } from './format.js'

const props = defineProps({
  clusterId: { type: String, required: true },
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const detail = ref(null)

const c = key => t(`visualGameAdmin.intentVideo.clusters.${key}`)
const productionVariant = { none: 'neutral', active: 'info', done: 'success', failed: 'danger' }
const coverageVariant = { covered: 'success', uncovered: 'neutral' }

/** 排队中的几个子状态（pending/prompting/prompt_ready/rendering）都算「排队中」这一个徽章色。 */
const ACTIVE = ['pending', 'prompting', 'prompt_ready', 'rendering']
const productionBucket = row => (row.production === 'none' ? 'none' : ACTIVE.includes(row.production) ? 'active' : row.production)

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    // 从列表点进来带着 scenario_id；直接开链接（没有这个 query）时退回目前唯一跑意图视频的剧本。
    const scenarioId = route.query.scenario_id ?? 'painted_skin_room'
    const data = await listIntentClusters({ scenario_id: scenarioId, cluster_id: props.clusterId })
    detail.value = data.items[0] ?? null
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function back() {
  router.push({ name: 'intent-video-clusters' })
}
</script>

<template>
  <div class="ivcd vga-page" data-insp-path="apps/visual-game-admin/src/pages/intent-video/ClusterDetail.vue">
    <PageHeader :title="c('detailTitle')">
      <template #actions>
        <Button variant="ghost" @click="back">
          <ArrowLeft :size="16" /> {{ c('back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <div v-else-if="detail === null" class="vga-state">
      <p>{{ c('notFound') }}</p>
    </div>
    <Section v-else>
      <div class="ivcd__detail">
        <div class="ivcd__kv">
          <span>{{ c('representative') }}</span>
          <strong>{{ detail.intent }}</strong>
        </div>
        <div class="ivcd__kv">
          <span>{{ t('visualGameAdmin.intentVideo.table.occurrences') }}</span>
          <span>{{ t('visualGameAdmin.intentVideo.clusters.occurrenceDetail', { n: detail.occurrences, v: detail.variants }) }}</span>
        </div>
        <div v-if="detail.request_id" class="ivcd__kv">
          <span>request_id</span><code>{{ detail.request_id }}</code>
        </div>
        <div class="ivcd__kv">
          <span>{{ c('productionColumn') }}</span>
          <Badge :variant="productionVariant[productionBucket(detail)]" size="sm">
            {{ detail.production === 'none' ? c('production.none') : queueStatusLabel(t, detail.production) }}
          </Badge>
        </div>
        <div class="ivcd__kv">
          <span>{{ c('coverageColumn') }}</span>
          <Badge :variant="coverageVariant[detail.coverage]" size="sm">{{ c(`coverage.${detail.coverage}`) }}</Badge>
        </div>
        <h4>{{ c('samples') }}</h4>
        <ul class="ivcd__samples">
          <li v-for="sample in detail.samples" :key="sample">{{ sample }}</li>
        </ul>
        <p v-if="detail.samples.length < detail.variants" class="ivcd__note">
          {{ t('visualGameAdmin.intentVideo.clusters.samplesTruncated', { n: detail.variants - detail.samples.length }) }}
        </p>
      </div>
    </Section>
  </div>
</template>

<style scoped>
.ivcd__detail { display: grid; gap: var(--drama-space-sm); }
.ivcd__detail h4 {
  margin: var(--drama-space-sm) 0 0; font-size: 12px; color: var(--drama-color-text-muted);
  text-transform: uppercase; letter-spacing: 0.03em;
}
.ivcd__kv { display: flex; gap: var(--drama-space-sm); align-items: baseline; font-size: 13px; }
.ivcd__kv > span:first-child { min-width: 92px; flex-shrink: 0; color: var(--drama-color-text-muted); font-size: 12px; }
.ivcd__kv code { font-family: var(--drama-font-family-mono, monospace); font-size: 11px; }
.ivcd__samples { margin: 0; padding-left: var(--drama-space-lg); display: grid; gap: 2px; }
.ivcd__samples li { font-size: 13px; line-height: 1.6; }
.ivcd__note { margin: 0; font-size: 12px; color: var(--drama-color-text-muted); }
</style>
